import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import type { ReactNode, MouseEventHandler } from "react";
import { Check, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
type TierVariant = "primary" | "muted" | "locked";
type TierFeature = {
  label: ReactNode;
  emphasized?: boolean;
};
export type TierCta = {
  kind: "link";
  label: string;
  href: string;
} | {
  kind: "current";
  label?: string;
} | {
  kind: "disabled";
  label: string;
} | {
  kind: "action";
  label: string;
  onClick: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
};
export interface TierCardProps {
  name: string;
  price: ReactNode;
  period?: string;
  tagline: string;
  features: TierFeature[];
  cta: TierCta;
  variant?: TierVariant;
  badge?: string;
  className?: string;
  appearance?: "default" | "simple";
}
export function TierCard(t0) {
  const $ = _c(59);
  const {
    name,
    price,
    period,
    tagline,
    features,
    cta,
    variant: t1,
    badge,
    className,
    appearance: t2
  } = t0;
  const variant = t1 === undefined ? "muted" : t1;
  const appearance = t2 === undefined ? "default" : t2;
  const isLocked = variant === "locked";
  const isSimple = appearance === "simple";
  const t3 = variant === "primary" && "border border-primary/50 shadow-[0_0_60px_-15px_rgba(var(--primary),0.3)] hover:shadow-[0_0_80px_-15px_rgba(var(--primary),0.4)] transition-shadow duration-300 z-10";
  const t4 = variant === "muted" && "border border-border shadow-sm";
  const t5 = variant === "locked" && "border border-border bg-card/60 shadow-sm";
  const t6 = isSimple && "rounded-2xl p-6 sm:p-7 shadow-none hover:shadow-none";
  let t7;
  if ($[0] !== className || $[1] !== t3 || $[2] !== t4 || $[3] !== t5 || $[4] !== t6) {
    t7 = cn("relative rounded-3xl bg-card p-10 flex flex-col", t3, t4, t5, t6, className);
    $[0] = className;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const cardClasses = t7;
  const t8 = isSimple ? "mb-6" : "mb-8";
  const t9 = variant === "primary" && "text-3xl text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-400";
  const t10 = variant === "muted" && "text-2xl bg-clip-text text-transparent bg-gradient-to-r from-muted-foreground to-foreground";
  const t11 = variant === "locked" && "text-2xl bg-clip-text text-transparent bg-gradient-to-r from-muted-foreground to-foreground";
  const t12 = isSimple && "bg-none text-lg font-semibold text-foreground";
  let t13;
  if ($[6] !== t10 || $[7] !== t11 || $[8] !== t12 || $[9] !== t9) {
    t13 = cn("font-extrabold", t9, t10, t11, t12);
    $[6] = t10;
    $[7] = t11;
    $[8] = t12;
    $[9] = t9;
    $[10] = t13;
  } else {
    t13 = $[10];
  }
  let t14;
  if ($[11] !== name || $[12] !== t13) {
    t14 = <h3 className={t13}>{name}</h3>;
    $[11] = name;
    $[12] = t13;
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  const t15 = variant === "primary" ? "text-6xl font-black text-foreground" : "text-6xl font-extrabold text-muted-foreground";
  const t16 = isSimple && "mt-6 text-5xl font-semibold text-foreground";
  let t17;
  if ($[14] !== t15 || $[15] !== t16) {
    t17 = cn("mt-4 flex items-baseline tracking-tight", t15, t16);
    $[14] = t15;
    $[15] = t16;
    $[16] = t17;
  } else {
    t17 = $[16];
  }
  let t18;
  if ($[17] !== isSimple || $[18] !== period) {
    t18 = period ? <span className={cn("ml-2 text-xl font-semibold text-muted-foreground tracking-normal", isSimple && "text-sm font-normal")}>{period}</span> : null;
    $[17] = isSimple;
    $[18] = period;
    $[19] = t18;
  } else {
    t18 = $[19];
  }
  let t19;
  if ($[20] !== price || $[21] !== t17 || $[22] !== t18) {
    t19 = <div className={t17}>{price}{t18}</div>;
    $[20] = price;
    $[21] = t17;
    $[22] = t18;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  const t20 = isSimple && "mt-6 border-t border-border/70 pt-5 text-sm font-medium text-foreground";
  let t21;
  if ($[24] !== t20) {
    t21 = cn("mt-4 text-muted-foreground", t20);
    $[24] = t20;
    $[25] = t21;
  } else {
    t21 = $[25];
  }
  let t22;
  if ($[26] !== t21 || $[27] !== tagline) {
    t22 = <p className={t21}>{tagline}</p>;
    $[26] = t21;
    $[27] = tagline;
    $[28] = t22;
  } else {
    t22 = $[28];
  }
  let t23;
  if ($[29] !== t14 || $[30] !== t19 || $[31] !== t22 || $[32] !== t8) {
    t23 = <div className={t8}>{t14}{t19}{t22}</div>;
    $[29] = t14;
    $[30] = t19;
    $[31] = t22;
    $[32] = t8;
    $[33] = t23;
  } else {
    t23 = $[33];
  }
  let t24;
  if ($[34] !== features || $[35] !== variant) {
    t24 = features.length > 0 && <ul className="mt-4 space-y-5 flex-1 text-sm sm:text-base">{features.map((feature, i) => <li key={i} className="flex gap-x-4 items-center"><FeatureIcon variant={variant} emphasized={feature.emphasized} /><span className={cn(variant === "primary" ? feature.emphasized ? "text-foreground font-bold" : "text-foreground" : "text-muted-foreground")}>{feature.label}</span></li>)}</ul>;
    $[34] = features;
    $[35] = variant;
    $[36] = t24;
  } else {
    t24 = $[36];
  }
  const t25 = features.length > 0 ? "mt-10" : "mt-auto";
  let t26;
  if ($[37] !== cta || $[38] !== variant) {
    t26 = <TierCtaButton cta={cta} variant={variant} />;
    $[37] = cta;
    $[38] = variant;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  if ($[40] !== t25 || $[41] !== t26) {
    t27 = <div className={t25}>{t26}</div>;
    $[40] = t25;
    $[41] = t26;
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  let t28;
  if ($[43] !== t23 || $[44] !== t24 || $[45] !== t27) {
    t28 = <>{t23}{t24}{t27}</>;
    $[43] = t23;
    $[44] = t24;
    $[45] = t27;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  const body = t28;
  let t29;
  if ($[47] !== badge || $[48] !== variant) {
    t29 = badge ? <TierBadge variant={variant}>{badge}</TierBadge> : null;
    $[47] = badge;
    $[48] = variant;
    $[49] = t29;
  } else {
    t29 = $[49];
  }
  let t30;
  if ($[50] !== body || $[51] !== isLocked || $[52] !== name || $[53] !== tagline) {
    t30 = isLocked ? <><div className="blur-[3px] select-none pointer-events-none" aria-hidden={true}>{body}</div><div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center pointer-events-none"><h3 className="text-3xl font-extrabold text-foreground">{name}</h3><p className="mt-2 text-sm text-muted-foreground max-w-xs">{tagline}</p></div></> : body;
    $[50] = body;
    $[51] = isLocked;
    $[52] = name;
    $[53] = tagline;
    $[54] = t30;
  } else {
    t30 = $[54];
  }
  let t31;
  if ($[55] !== cardClasses || $[56] !== t29 || $[57] !== t30) {
    t31 = <div className={cardClasses}>{t29}{t30}</div>;
    $[55] = cardClasses;
    $[56] = t29;
    $[57] = t30;
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  return t31;
}
function TierBadge(t0) {
  const $ = _c(6);
  const {
    variant,
    children
  } = t0;
  const t1 = variant === "primary" && "bg-primary text-primary-foreground shadow-lg shadow-primary/30";
  const t2 = (variant === "muted" || variant === "locked") && "bg-muted text-muted-foreground border border-border";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = cn("text-xs font-black px-4 py-1.5 rounded-full uppercase tracking-wider", t1, t2);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== children || $[4] !== t3) {
    t4 = <div className="absolute top-0 right-8 transform -translate-y-1/2 z-20"><div className={t3}>{children}</div></div>;
    $[3] = children;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
function FeatureIcon(t0) {
  const $ = _c(6);
  const {
    variant,
    emphasized
  } = t0;
  if (variant === "locked") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Lock className="h-5 w-5 flex-none text-muted-foreground" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (variant === "primary" && emphasized) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="bg-primary/20 rounded-full p-1 animate-pulse"><Check className="h-4 w-4 flex-none text-primary" /></div>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const t1 = variant === "primary" ? "text-primary" : "text-muted-foreground";
  let t2;
  if ($[2] !== t1) {
    t2 = cn("h-6 w-6 flex-none", t1);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <Check className={t2} />;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function TierCtaButton(t0) {
  const $ = _c(14);
  const {
    cta,
    variant
  } = t0;
  if (cta.kind === "link") {
    let t1;
    if ($[0] !== cta.href || $[1] !== cta.label) {
      t1 = <Button asChild={true} className="w-full h-14 rounded-2xl text-lg font-bold shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-all hover:scale-[1.02] active:scale-[0.98]"><Link href={cta.href}>{cta.label}</Link></Button>;
      $[0] = cta.href;
      $[1] = cta.label;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (cta.kind === "action") {
    let t1;
    if ($[3] !== cta.disabled || $[4] !== cta.label || $[5] !== cta.onClick) {
      t1 = <Button className="w-full h-14 rounded-2xl text-lg font-bold" onClick={cta.onClick} disabled={cta.disabled}>{cta.label}</Button>;
      $[3] = cta.disabled;
      $[4] = cta.label;
      $[5] = cta.onClick;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    return t1;
  }
  if (cta.kind === "current") {
    const t1 = cta.label ?? "Current Plan";
    let t2;
    if ($[7] !== t1) {
      t2 = <Button variant="outline" asChild={true} className="w-full h-14 rounded-2xl text-lg opacity-70 cursor-default hover:bg-transparent"><span>{t1}</span></Button>;
      $[7] = t1;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    return t2;
  }
  const t1 = variant === "locked" ? "opacity-60" : "opacity-70";
  let t2;
  if ($[9] !== t1) {
    t2 = cn("w-full h-14 rounded-2xl text-lg cursor-not-allowed", t1);
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== cta.label || $[12] !== t2) {
    t3 = <Button variant="outline" disabled={true} className={t2}>{cta.label}</Button>;
    $[11] = cta.label;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}